import { useCallback, useMemo, useState } from 'react'

import {
  Card,
  CardContent,
  CardHeading,
  ChartControls,
  ChartFilters,
  LabeledSwatch,
  MultiLineChart,
} from '@/components/ui'
import type { MultiSeriesDataPoint } from '@/components/ui/chart/types'
import type { DateRangeType } from '@/components/ui/chart/utils/exportChart'
import { transactionFilterSchema } from '@/lib/schemas/transactionFilterSchema'
import { type Transaction, useProject } from '@/modules/projects'
import { useFilters } from '@/stores'
import { useFiltersStore } from '@/stores/filters/context'
import type { FiltersStore } from '@/stores/filters/types'
import {
  formatDate,
  numberToCurrency,
  parseCalendarDate,
  semanticColors,
} from '@/utils'

interface MonthlyTooltipLine {
  key: string
  label: string
  color: string
}

interface ChartDataPoint {
  date: string
  inflow: number
  outflow: number
}

interface MonthlyData {
  [key: string]: ChartDataPoint
}

interface ChartControlsState {
  containerRef: React.RefObject<HTMLDivElement | null>
  isFullscreen: boolean
  toggleFullscreen: () => Promise<void>
  fullscreenError?: string | null
  fullscreenSupported?: boolean
}

interface TransactionsChartsProps {
  transactions: Transaction[]
}
export function TransactionsCharts({ transactions }: TransactionsChartsProps) {
  const project = useProject()
  const filters = useFilters()
  const dateRange = useFiltersStore((state: FiltersStore) => state.dateRanges)
  const [chartControls, setChartControls] = useState<ChartControlsState | null>(
    null,
  )
  const chartData = useMemo(() => {
    if (!transactions.length) return []

    const groupedByMonth = transactions.reduce(
      (acc: MonthlyData, tx: Transaction) => {
        // Internal transfers are not money in or out of the case, so keep them off the chart.
        if (tx.internalTransfer) return acc
        const extractedMonth = tx.date?.match(/^\d{4}-\d{2}/)?.[0]
        if (!extractedMonth) return acc

        const monthKey = `${extractedMonth}-01T00:00:00`

        if (!acc[monthKey]) {
          acc[monthKey] = { date: monthKey, inflow: 0, outflow: 0 }
        }
        if (tx.amount > 0) {
          acc[monthKey].inflow += tx.amount
        } else {
          acc[monthKey].outflow += Math.abs(tx.amount)
        }
        return acc
      },
      {},
    )

    const sortedMonths = Object.values(groupedByMonth).sort(
      (a: ChartDataPoint, b: ChartDataPoint) =>
        parseCalendarDate(a.date).getTime() -
        parseCalendarDate(b.date).getTime(),
    )

    return sortedMonths
  }, [transactions])

  const dataDateRange = useMemo(() => {
    if (!chartData.length) return undefined
    return {
      from: chartData[0].date,
      to: chartData[chartData.length - 1].date,
    }
  }, [chartData])

  const chartSeries = useMemo(
    () => [
      { key: 'inflow', label: 'Inflows', color: semanticColors.inflow },
      { key: 'outflow', label: 'Outflows', color: semanticColors.outflow },
    ],
    [],
  )

  const renderMonthlyTooltip = useCallback(
    (hoveredData: MultiSeriesDataPoint | null, lines: MonthlyTooltipLine[]) => {
      if (!hoveredData?.dateObj) return null

      const sortedLines = lines
        .map((line) => ({
          ...line,
          value: Number(hoveredData[line.key] ?? 0),
        }))
        .sort((a, b) => b.value - a.value)

      return (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">
            {formatDate(hoveredData.dateObj, 'MMMM yyyy')}
          </div>
          {sortedLines.map((line) => (
            <LabeledSwatch
              key={line.key}
              color={line.color}
              className="gap-2 text-sm"
              label={
                <span className="max-w-30 truncate font-medium">
                  {line.label}:
                </span>
              }
              value={numberToCurrency(line.value)}
            />
          ))}
        </div>
      )
    },
    [],
  )

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-4">
        <ChartFilters>
          {chartControls && (
            <ChartControls
              containerRef={chartControls.containerRef}
              isFullscreen={chartControls.isFullscreen}
              toggleFullscreen={chartControls.toggleFullscreen}
              fullscreenError={chartControls.fullscreenError}
              fullscreenSupported={chartControls.fullscreenSupported}
              filename={`${project?.slug}-transaction-timeline`}
              projectName={project?.name}
              title="Transaction timeline"
              filters={filters}
              schema={transactionFilterSchema}
              dateRange={dateRange as DateRangeType}
              dataDateRange={dataDateRange}
              size="md"
              floating={false}
            />
          )}
        </ChartFilters>
      </div>
      <Card className="w-full">
        <CardHeading title="Transaction timeline" />
        <CardContent>
          <div style={{ height: '320px' }}>
            {chartData.length > 0 ? (
              <MultiLineChart
                data={chartData}
                series={chartSeries}
                height={320}
                legendPosition="bottom"
                interactiveLegend={false}
                onChartReady={(params) => setChartControls(params)}
                renderTooltip={renderMonthlyTooltip}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
